import CustomerWorkspaceShell from "./CustomerWorkspaceShell";
import SharedCustomerFields from './SharedCustomerFields';
import CustomerContext from './CustomerContext';
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRightLeft,
  Check,
  LockKeyhole,
  RefreshCw,
  Share2,
  X,
} from "lucide-react";
import axios from "src/common/axiosInstance";
import "./CustomerSharing.css";

const errorMessage = (error) =>
  Object.values(error.response?.data?.errors || {}).flat()[0] ||
  error.response?.data?.message ||
  "Sharing could not be loaded. Please try again.";
const modeLabel = {
  local: "Only in this app",
  off: "Not shared",
  copy: "Independent copy",
  sync: "Keep in sync",
  legacy: "Existing shared link",
};

function SharingDialog({ title, onClose, children, profile = false }) {
  const ref = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    ref.current?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
      if (event.key !== "Tab") return;
      const elements = [
        ...ref.current.querySelectorAll(
          'button:not(:disabled), a[href], summary, textarea:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex="0"]',
        ),
      ].filter(element => !element.closest("[hidden]"));
      if (!elements.length) {
        event.preventDefault();
        return;
      }
      const first = elements[0],
        last = elements[elements.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === ref.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const node = ref.current;
    node.addEventListener("keydown", keydown);
    return () => {
      node.removeEventListener("keydown", keydown);
      previous?.focus?.();
    };
  }, [onClose]);
  return createPortal(
    <div className={`customer-share-overlay ${profile ? "cw-workspace-overlay" : ""}`}>
      <section
        ref={ref}
        className={`customer-share customer-share-dialog ${profile ? "cw-workspace-dialog" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={profile ? undefined : titleId}
        aria-label={profile ? title : undefined}
        tabIndex={-1}
      >
        {!profile && <header>
          <span className="customer-share-icon">
            <ArrowRightLeft size={21} />
          </span>
          <div>
            <p className="customer-share-eyebrow">{profile ? 'CUSTOMER PROFILE' : 'CUSTOMER SHARING'}</p>
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className="customer-share-close"
            aria-label="Close sharing"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>}
        {children}
      </section>
    </div>,
    document.body,
  );
}

function SyncReadiness({ value }) {
  if (!value) return null;
  return (
    <div className="customer-share-notice" role="status">
      <RefreshCw size={17} aria-hidden="true" />
      <div><p>{value.message}</p>{value.last_check && <small>Last transfer check: {new Date(value.last_check.checked_at).toLocaleString()} · {({ checked: 'Check completed', pending: 'More updates pending', needs_review: 'Some updates need review', failed: 'Check failed' })[value.last_check.status] || 'Unknown result'}. This is the last check, not a guarantee that newer edits have arrived.</small>}</div>
    </div>
  );
}

function Profile({ value, title }) {
  if (!value) return null;
  const identifiers = value.identifiers || [];
  return (
    <div className="customer-share-profile">
      <strong>{title}</strong>
      <span>{value.name || "Unnamed customer"}</span>
      <small>
        {[value.email, value.phone, ...identifiers.map((i) => i.value)]
          .filter(Boolean)
          .join(" · ") || "No email or international phone yet"}
      </small>
    </div>
  );
}

export function CustomerSharingButton({
  recordId,
  name,
  endpoint,
  core = false,
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!recordId) return null;
  return (
    <>
      <button
        type="button"
        className="customer-share-trigger"
        onClick={() => setOpen(true)}
        aria-label={`App sharing for ${name || "customer"}`}
      >
        <Share2 size={15} />
        <span>App sharing</span>
      </button>
      {open && (
        <SharingDialog title={name || "Share customer profile"} onClose={close}>
          <RecordSharing
            endpoint={endpoint || `/customer-sharing/records/${recordId}`}
            core={core}
          />
          {!core && <SharedCustomerFields key={recordId} recordId={recordId} />}
        </SharingDialog>
      )}
    </>
  );
}

/** Direct profile entry; opening it never enables sharing. */
export function CustomerProfileButton({ recordId, name, onOpen }) {
  const [view, setView] = useState("sales");
  const sections = { profile: "profile", sales: "crm", whatsapp: "whatsapp", meetings: "meetings", email: "email", calls: "calls", ai: "memory", activity: "crm" };
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const close = useCallback(() => { setOpen(false); setSharing(false); }, []);
  if (!recordId) return null;
  return <>
    <button type="button" className="customer-share-trigger" aria-label={`Customer profile for ${name || 'customer'}`} onClick={() => { if (onOpen) { onOpen(); return; } setView("sales"); setOpen(true); }}>
      <ArrowRightLeft size={15} aria-hidden="true" /><span>Customer profile</span>
    </button>
    {open && <SharingDialog title={name || 'Customer profile'} onClose={close} profile>
      <CustomerWorkspaceShell name={name} view={view} onViewChange={setView} actions={
        <button type="button" className="customer-share-close" aria-label="Close customer profile" onClick={close}><X size={20} /></button>
      }>
        <div className="customer-share-body cw-sharing-tools">
          <button type="button" className="customer-share-trigger" aria-expanded={sharing} onClick={() => setSharing(!sharing)}>Manage app sharing</button>
        </div>
        {sharing && <RecordSharing endpoint={`/customer-sharing/records/${encodeURIComponent(recordId)}`} core={false} />}
        <CustomerContext key={`${recordId}:${view}`} recordId={recordId} name={name} initialSection={sections[view]} compact autoOpenSingle={view !== 'sales'} />
      </CustomerWorkspaceShell>
    </SharingDialog>}
  </>;
}

function RecordSharing({ endpoint, core }) {
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const [mode, setMode] = useState("copy"),
    [selected, setSelected] = useState({}),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setData(null);
    setError("");
    axios
      .get(endpoint)
      .then(({ data: response }) => {
        if (!current) return;
        const value = response.data;
        setData(value);
        setMode(value.mode === "sync" ? "sync" : "copy");
        setSelected(
          Object.fromEntries(
            (value.destinations || []).map((item) => [item.module, item.mode]),
          ),
        );
      })
      .catch((e) => {
        if (current) setError(errorMessage(e));
      });
    return () => {
      current = false;
    };
  }, [endpoint, revision]);
  const save = async (payload) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.put(endpoint, payload);
      setMessage(
        payload.mode === "local" || payload.mode === "off"
          ? "Updates stopped. Existing copies stay in their apps."
          : core
            ? "Transfer saved. The destination app will import it on its next sync."
            : "Customer profile shared with your Auto Appy organisation.",
      );
      setRevision((v) => v + 1);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="customer-share-body">
      <p className="customer-share-intro">
        One customer, with control over where their details go.
      </p>
      <div className="customer-share-notice">
        <LockKeyhole size={17} />
        <p>
          Shares name, email and phone. Messages, notes, sales stages and
          assignments stay in their original app. Team access still follows each
          person’s role.
        </p>
      </div>
      {error && (
        <div className="customer-share-error" role="alert">
          {error}
          <button
            type="button"
            disabled={busy}
            onClick={() => setRevision((v) => v + 1)}
          >
            Refresh details
          </button>
        </div>
      )}
      {message && (
        <p className="customer-share-success" role="status">
          <Check size={16} />
          {message}
        </p>
      )}
      {!data && !error && <p role="status">Loading sharing details…</p>}
      {data && (
        <>
          {!core && <SyncReadiness value={data.sync_readiness} />}
          {!data.can_share && (
            <p className="customer-share-muted">
              An owner, admin or manager can share customers you work with.
            </p>
          )}
          {core ? (
            <>
              <h3>Choose destination apps</h3>
              <p className="customer-share-muted">
                Leave an app off to keep this person only in Auto Appy. New
                copies arrive unassigned, ready for an admin to assign.
              </p>
              <div className="customer-share-destinations">
                {(data.modules || []).map((module) => {
                  const existing = data.destinations?.find(
                    (item) => item.module === module.key,
                  );
                  return (
                    <div
                      className="customer-share-destination"
                      key={module.key}
                    >
                      <div>
                        <strong>{module.name}</strong>
                        <small>
                          {existing?.delivery_error ||
                            (existing?.legacy
                              ? "Existing shared link"
                              : existing?.delivered_at
                                ? "Delivered"
                                : existing && existing.mode !== "off"
                                  ? "Waiting for app sync"
                                  : "Not shared")}
                        </small>
                      </div>
                      <label>
                        <span className="customer-share-sr">
                          Sharing with {module.name}
                        </span>
                        <select
                          disabled={!data.can_share || busy}
                          value={selected[module.key] || "off"}
                          onChange={(e) =>
                            setSelected({
                              ...selected,
                              [module.key]: e.target.value,
                            })
                          }
                        >
                          <option value="off">Not shared / stop updates</option>
                          <option value="copy">Copy once</option>
                          <option value="sync">Keep in sync</option>
                        </select>
                      </label>
                      <button
                        className="customer-share-primary"
                        type="button"
                        disabled={!data.can_share || busy}
                        onClick={() =>
                          save({
                            module: module.key,
                            mode: selected[module.key] || "off",
                          })
                        }
                      >
                        {existing?.delivery_error ? "Retry transfer" : "Apply"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="customer-share-status">
                <span className="customer-share-badge">
                  {modeLabel[data.mode] || data.mode}
                </span>
                {data.pending && <span>Update pending</span>}
              </div>
              {(data.last_error || data.refresh_error) && (
                <p className="customer-share-error">
                  {data.last_error || data.refresh_error}
                </p>
              )}
              <div className="customer-share-profiles">
                <Profile value={data.profile} title="In this app" />
                <Profile
                  value={data.shared_profile}
                  title="In your organisation directory"
                />
              </div>
              <fieldset
                disabled={!data.can_share || busy}
                className="customer-share-options"
              >
                <legend>Share with your Auto Appy organisation</legend>
                <label className={mode === "copy" ? "selected" : ""}>
                  <input
                    type="radio"
                    name="customer-share-mode"
                    value="copy"
                    checked={mode === "copy"}
                    onChange={() => setMode("copy")}
                  />
                  <span>
                    <strong>Copy once</strong>
                    <small>
                      Send these details now. Each app can then manage its copy
                      independently.
                    </small>
                  </span>
                </label>
                <label className={mode === "sync" ? "selected" : ""}>
                  <input
                    type="radio"
                    name="customer-share-mode"
                    value="sync"
                    checked={mode === "sync"}
                    onChange={() => setMode("sync")}
                  />
                  <span>
                    <strong>Keep in sync</strong>
                    <small>
                      Share future profile edits through Auto Appy. Conflicting
                      edits are held for review.
                    </small>
                  </span>
                </label>
              </fieldset>
              <p className="customer-share-muted">
                Matching email or international phone links the same person. A
                first share keeps an existing Auto Appy profile’s name. Choose
                any further destination apps from that contact in Auto Appy.
              </p>
              <div className="customer-share-actions">
                <button
                  type="button"
                  disabled={!data.can_share || busy || data.mode === "local"}
                  onClick={() => save({ mode: "local" })}
                >
                  Keep this copy local
                </button>
                <button
                  type="button"
                  className="customer-share-primary"
                  disabled={!data.can_share || busy}
                  onClick={() =>
                    save({
                      mode,
                      expected_version: data.shared_profile?.version || null,
                    })
                  }
                >
                  {busy
                    ? "Saving…"
                    : mode === "sync"
                      ? "Share and keep in sync"
                      : "Share a copy"}
                </button>
              </div>
            </>
          )}
          <p className="customer-share-footnote">
            Sharing copies information. It never deletes the original record.
          </p>
        </>
      )}
    </div>
  );
}

export function CustomerSharingSettings({
  endpoint = "/customer-sharing/settings",
  core = false,
  appName = "this app",
}) {
  const [data, setData] = useState(null),
    [automatic, setAutomatic] = useState(false),
    [targets, setTargets] = useState([]);
  const [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setData(null);
    setError("");
    axios
      .get(endpoint)
      .then(({ data: response }) => {
        if (current) {
          setData(response.data);
          setAutomatic(response.data.automatic);
          setTargets(response.data.targets || []);
        }
      })
      .catch((e) => {
        if (current) setError(errorMessage(e));
      });
    return () => {
      current = false;
    };
  }, [endpoint, revision]);
  const save = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.put(endpoint, {
        automatic,
        expected_version: data.version,
        ...(core ? { targets } : {}),
      });
      setMessage("Sharing defaults saved. Existing records are unchanged.");
      setRevision((v) => v + 1);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { data: response } = await axios.post("/customer-sharing/refresh");
      const status = response.data?.status;
      if (status === "needs_review") {
        setError("Some customer updates could not be applied. Review the affected customers’ App sharing details before retrying.");
      } else if (status === "pending") {
        setMessage("More updates are pending. The next background check will continue the transfer.");
      } else if (status === "busy") {
        setMessage("A transfer check is already running. Check again shortly.");
      } else if (status === "checked" || response.data?.refreshed === true) {
        setMessage("Transfer check completed. Pending local edits may require another check; this does not enable sharing for additional customers.");
      } else {
        setError(response.data?.sync_readiness?.message || "Transfers could not be verified. Check again shortly.");
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="customer-share customer-share-settings">
      <header>
        <span className="customer-share-icon">
          <ArrowRightLeft size={21} />
        </span>
        <div>
          <p className="customer-share-eyebrow">CONNECTED CUSTOMER DIRECTORY</p>
          <h2>Customer sharing</h2>
        </div>
      </header>
      <div className="customer-share-body">
        <p className="customer-share-intro">
          Choose what happens to new contacts
          {appName === "Sales" ? " and leads" : ""} in {appName}.
        </p>
        {error && (
          <div role="alert" className="customer-share-error">
            {error}
            <button type="button" onClick={() => setRevision((v) => v + 1)}>
              Try again
            </button>
          </div>
        )}
        {message && (
          <p role="status" className="customer-share-success">
            <Check size={16} />
            {message}
          </p>
        )}
        {!data && !error && <p role="status">Loading preferences…</p>}
        {data && (
          <>
            {!core && <SyncReadiness value={data.sync_readiness} />}
            <fieldset
              className="customer-share-options"
              disabled={!data.can_manage || busy}
            >
              <legend>Default for new records</legend>
              <label className={!automatic ? "selected" : ""}>
                <input
                  type="radio"
                  name={`sharing-default-${appName}`}
                  checked={!automatic}
                  onChange={() => setAutomatic(false)}
                />
                <span>
                  <strong>Only when I share</strong>
                  <small>
                    New records stay here until someone uses App sharing.
                  </small>
                </span>
                <span className="customer-share-badge">Recommended</span>
              </label>
              <label className={automatic ? "selected" : ""}>
                <input
                  type="radio"
                  name={`sharing-default-${appName}`}
                  checked={automatic}
                  onChange={() => setAutomatic(true)}
                />
                <span>
                  <strong>Always share new records</strong>
                  <small>
                    {core
                      ? "Keep new Auto Appy contacts in sync with the apps selected below."
                      : "Keep new customer profiles in sync with Auto Appy."}
                  </small>
                </span>
              </label>
              {core && automatic && (
                <div className="customer-share-targets">
                  {data.modules?.map((m) => (
                    <label key={m.key}>
                      <input
                        type="checkbox"
                        checked={targets.includes(m.key)}
                        onChange={(e) =>
                          setTargets(
                            e.target.checked
                              ? [...targets, m.key]
                              : targets.filter((key) => key !== m.key),
                          )
                        }
                      />
                      {m.name}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
            <div className="customer-share-notice">
              <LockKeyhole size={17} />
              <p>
                Existing customers stay as they are. Only owners and admins
                change defaults. Sharing never changes who can access a customer
                or conversation.
              </p>
            </div>
            <div className="customer-share-actions">
              {!core && data.can_manage && (
                <button type="button" onClick={refresh} disabled={busy || data.sync_readiness?.can_check === false}>
                  <RefreshCw size={14} />
                  Check transfers now
                </button>
              )}
              <button
                type="button"
                className="customer-share-primary"
                disabled={
                  !data.can_manage ||
                  busy ||
                  (core && automatic && !targets.length)
                }
                onClick={save}
              >
                {busy ? "Saving…" : "Save sharing defaults"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
